import React, { useState, useEffect, useCallback, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import TablePagination from "./TablePagination";
import { toast } from "react-toastify";
import { getExposList } from "../Apis/ExpoAPIs";
import { getClientFollowups, getClientFollowupHistory } from "../Apis/ClientAPIs";
import { formatDate, formatDateTime } from "../helper/DateHelper";

const UnwantedEnquiriesLayer = () => {
  const [loading, setLoading] = useState(true);
  const [followups, setFollowups] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Dropdown options
  const [expoOptions, setExpoOptions] = useState([]);
  const [selectedExpo, setSelectedExpo] = useState({ value: "", label: "All Expos" });
  const expoSearchTimeout = useRef(null);

  // Modals state
  const [selectedClientForModal, setSelectedClientForModal] = useState(null);
  const [showClientDetailModal, setShowClientDetailModal] = useState(false);
  const [showClientHistoryModal, setShowClientHistoryModal] = useState(false);
  const [clientFollowupHistory, setClientFollowupHistory] = useState([]);

  // Debounce search term to limit API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(0);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Load expos list
  const loadExpos = async (searchVal = "") => {
    try {
      const params = { limit: 1000 };
      if (searchVal) params.search = searchVal;
      const expoRes = await getExposList(params);
      if (expoRes?.status) {
        const expos = expoRes.response?.data || expoRes.response || [];
        const options = [
          { value: "", label: "All Expos" },
          ...expos.map((e) => ({
            value: e._id || e.id,
            label: e.expoName || e.name,
          }))
        ];
        setExpoOptions(options);
      }
    } catch (err) {
      console.error("Failed to load expos list:", err);
      toast.error("Failed to load expos dropdown list");
    }
  };

  // Load expos list on mount
  useEffect(() => {
    loadExpos("");
  }, []);

  const handleExpoSearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (expoSearchTimeout.current) clearTimeout(expoSearchTimeout.current);
      expoSearchTimeout.current = setTimeout(() => {
        loadExpos(inputValue);
      }, 500);
    }
  };

  // Fetch followups with status "UnWanted"
  const fetchUnwantedEnquiries = useCallback(async () => {
    if (selectedExpo === null) {
      setFollowups([]);
      setTotalItems(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: rowsPerPage,
        status: "UnWanted",
        expoId: selectedExpo.value || undefined,
        search: debouncedSearchTerm || undefined,
      };

      const result = await getClientFollowups(params);
      if (result.status) {
        const data = Array.isArray(result.response?.data)
          ? result.response.data
          : (result.response?.data?.docs || result.response?.docs || []);
        const total = typeof result.response?.total === 'number'
          ? result.response.total
          : (result.response?.data?.total || result.response?.total || 0);

        setFollowups(data);
        setTotalItems(total);
      } else {
        setFollowups([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error("Error fetching unwanted enquiries:", error);
      toast.error("Failed to fetch unwanted enquiries list");
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, selectedExpo, debouncedSearchTerm]);

  useEffect(() => {
    fetchUnwantedEnquiries();
  }, [fetchUnwantedEnquiries]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedExpo({ value: "", label: "All Expos" });
    setCurrentPage(0);
  };

  const openHistoryModal = async (item) => {
    const clientData = item.client && item.clientId ? item.client : {};
    setSelectedClientForModal({
      ...clientData,
      _id: clientData._id || item._id,
      id: clientData.id || item.id,
      companyName: clientData.companyName || item.companyName || "N/A",
      keyPerson: clientData.keyPerson || item.name || "N/A",
      mobile1: clientData.mobile1 || item.mobileNo || "N/A",
      mobile2: clientData.mobile2 || "",
      mobile3: clientData.mobile3 || "",
      email: clientData.email || item.email || "N/A",
      // ratings: clientData.ratings || item.ratings || "OTHER EXHIBITOR",
      designation: clientData.designation || item.designation || "N/A",
      address: clientData.address || item.address || "N/A",
      city: clientData.city || item.city || "",
      pincode: clientData.pincode || item.pincode || "",
      landline: clientData.landline || item.landline || "N/A",
      websiteLink: clientData.websiteLink || item.websiteLink || "",
      products: (clientData.products && clientData.products.length > 0) ? clientData.products : (item.products || []),
      interestedLocations: (clientData.interestedLocations && clientData.interestedLocations.length > 0) ? clientData.interestedLocations : (item.interestedLocations || []),
      segment: clientData.segment || item.segment || ""
    });

    const targetClientId = item.clientId?._id || item.clientId?.id || item.clientId;
    const expoId = item.expoId?._id || item.expoId?.id || item.expoId;
    if (targetClientId && expoId) {
      try {
        const historyRes = await getClientFollowupHistory(targetClientId, {
          expoId: expoId,
          managerId: item.managerId
        });
        if (historyRes.status) {
          setClientFollowupHistory(historyRes.response?.data || historyRes.response || []);
        } else {
          setClientFollowupHistory([]);
        }
      } catch (err) {
        console.error("Error fetching followup history:", err);
        setClientFollowupHistory([]);
      }
    } else {
      // Standalone followup history
      const mappedHistory = (item.statusDetails || []).map(d => ({
        status: d.status,
        remarks: d.remarks,
        date: d.date,
        createdAt: d.createdAt,
        createdByName: d.createdByName || (d.createdBy === item.managerId ? item.manager?.name : 'Admin') || 'Manager'
      })).sort((a, b) => new Date(b.createdAt || b.date).getTime() - new Date(a.createdAt || a.date).getTime());
      setClientFollowupHistory(mappedHistory);
    }
    setShowClientHistoryModal(true);
  };

  const totalPages = Math.ceil(totalItems / rowsPerPage) || 1;

  return (
    <div className="card h-100 p-0 radius-12 overflow-hidden border-0 shadow-sm">
      {/* Header */}
      <div
        className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between"
        style={{ background: "linear-gradient(135deg, #000000, #bb0007, #ed1c24)" }}
      >
        <h6 className="text-white mb-0 fw-bold">Unwanted Enquiries</h6>
      </div>

      {/* Filter Bar */}
      <div
        className="px-24 py-16 border-bottom"
        style={{ backgroundColor: "#fafafa" }}
      >
        <div className="d-flex align-items-end flex-wrap gap-3">
          {/* Expo Selection */}
          <div style={{ minWidth: "250px", flex: "1 1 250px", maxWidth: "320px" }}>
            <label
              className="form-label text-xs fw-bold text-secondary-light uppercase mb-1"
              style={{ letterSpacing: "0.5px" }}
            >
              Expo Name
            </label>
            <Select
              options={expoOptions}
              value={selectedExpo}
              onChange={(val) => {
                setSelectedExpo(val);
                setCurrentPage(0);
              }}
              onInputChange={handleExpoSearch}
              isClearable={false}
              placeholder="Select Expo"
              styles={selectStyles()}
            />
          </div>

          {/* Search Input */}
          <div style={{ minWidth: "220px", flex: "1 1 220px", maxWidth: "280px" }}>
            <label
              className="form-label text-xs fw-bold text-secondary-light uppercase mb-1"
              style={{ letterSpacing: "0.5px" }}
            >
              Search
            </label>
            <div className="position-relative">
              <input
                type="text"
                className="form-control radius-8 ps-40"
                style={{ height: "44px" }}
                placeholder="Search Name, Company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Icon
                icon="ion:search-outline"
                className="position-absolute top-50 translate-middle-y text-secondary-light"
                style={{ fontSize: "18px", left: "12px" }}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-danger h-44-px radius-8 fw-bold d-flex align-items-center justify-content-center gap-2"
              onClick={handleClearFilters}
              title="Clear Filters"
              style={{ minWidth: "100px" }}
            >
              <Icon icon="solar:restart-bold" className="text-xl" />
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Table Data */}
      <div className="card-body p-24">
        <div className="table-responsive border radius-8 bg-white overflow-x-auto shadow-sm">
          <table className="table bordered-table sm-table mb-0" style={{ minWidth: '1000px' }}>
            <thead>
              <tr style={{ background: "linear-gradient(135deg, #000000, #4f1818, #ed1c24)" }}>
                <th scope="col" className="fw-bold text-white py-12 px-16" style={{ width: '60px' }}>S.No</th>
                <th scope="col" className="fw-bold text-white py-12" style={{ width: '220px' }}>Company Name</th>
                <th scope="col" className="fw-bold text-white py-12" style={{ width: '180px' }}>Key Person</th>
                <th scope="col" className="fw-bold text-white py-12" style={{ width: '200px' }}>Mobile Number</th>
                {/* <th scope="col" className="fw-bold text-white py-12" style={{ width: '180px' }}>Email</th> */}
                {/* <th scope="col" className="fw-bold text-white py-12 text-center" style={{ width: '120px' }}>Ratings</th> */}
                <th scope="col" className="fw-bold text-white py-12" style={{ width: '120px' }}>Source</th>
                <th scope="col" className="fw-bold text-white py-12" style={{ minWidth: '200px' }}>Remarks</th>
                <th scope="col" className="fw-bold text-white py-12" style={{ width: '120px' }}>Date</th>
                <th scope="col" className="fw-bold text-white py-12 text-center" style={{ width: '100px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center py-24">
                    <div className="spinner-border text-danger" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : !selectedExpo ? (
                <tr>
                  <td colSpan="10" className="text-center py-48 text-secondary-light">
                    <Icon icon="solar:info-circle-bold-duotone" className="text-warning mb-16" style={{ fontSize: "48px" }} />
                    <h6 className="mb-0 text-dark fw-bold">Please select an expo from the dropdown to see enquiries.</h6>
                  </td>
                </tr>
              ) : followups.length > 0 ? (
                followups.map((item, index) => {
                  const company = item.companyName || item.client?.companyName || "N/A";
                  const name = item.name || item.client?.keyPerson || "N/A";
                  const mobile = item.mobileNo || item.client?.mobile1 || "N/A";
                  // const email = item.email || item.client?.email || "N/A";
                  // const ratings = item.ratings || item.client?.ratings || "OTHER EXHIBITOR";

                  return (
                    <tr key={item._id || index}>
                      <td className="px-16">{currentPage * rowsPerPage + index + 1}</td>
                      <td className="fw-semibold text-dark">{company}</td>
                      <td>{name}</td>
                      <td>{mobile}</td>
                      {/* <td>{email}</td> */}
                      {/* <td className="text-center">
                        <span className="badge bg-warning-100 text-warning-800 px-10 py-4 radius-4 fw-bold">
                          {ratings} ★
                        </span>
                      </td> */}
                      <td>
                        <span className={`badge px-10 py-4 radius-4 fw-semibold text-xs text-uppercase ${item.source === 'mobile-admin' ? 'bg-primary-100 text-primary-800' : 'bg-info-100 text-info-800'
                          }`}>
                          {item.source || "web"}
                        </span>
                      </td>
                      <td style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                        {item.remarks || "No remarks"}
                      </td>
                      <td>{formatDate(item.createdAt || item.updatedAt)}</td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button
                            type="button"
                            className="fw-medium w-36-px h-36-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            style={{ backgroundColor: "#f3e8ff", color: "#7c3aed", border: "1px solid #ddd6fe" }}
                            title="Followup History"
                            onClick={() => openHistoryModal(item)}
                          >
                            <Icon icon="lucide:history" className="text-lg" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="10" className="text-center py-48 text-secondary-light">
                    <Icon icon="solar:folder-error-bold-duotone" className="text-muted mb-16" style={{ fontSize: "48px" }} />
                    <h6 className="mb-0 text-dark fw-bold">No unwanted enquiries found.</h6>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {selectedExpo && followups.length > 0 && (
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            totalRecords={totalItems}
          />
        )}
      </div>

      {/* Client Detail Modal */}
      {showClientDetailModal && selectedClientForModal && (
        <div
          className="modal fade show d-block animate__animated animate__fadeIn"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg animate__animated animate__zoomIn">
            <div className="modal-content radius-16 border-0 shadow-lg">
              <div
                className="modal-header border-bottom py-16 px-24"
                style={{ background: "linear-gradient(135deg, #000000, #bb0007, #ed1c24)" }}
              >
                <h6 className="modal-title text-white mb-0">
                  Client Details: {selectedClientForModal.companyName}
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowClientDetailModal(false)}
                ></button>
              </div>
              <div className="modal-body p-24" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <div className="row gy-4">
                  <div className="col-md-6">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Company Name</span>
                    <div className="p-12 bg-neutral-50 radius-8 border fw-bold text-dark">{selectedClientForModal.companyName || "N/A"}</div>
                  </div>
                  <div className="col-md-6">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Key Person Name</span>
                    <div className="p-12 bg-neutral-50 radius-8 border fw-semibold text-dark">{selectedClientForModal.keyPerson || "N/A"}</div>
                  </div>
                  <div className="col-md-4">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Mobile Number 1</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark">{selectedClientForModal.mobile1 || "N/A"}</div>
                  </div>
                  <div className="col-md-4">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Mobile Number 2</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark">{selectedClientForModal.mobile2 || "N/A"}</div>
                  </div>
                  <div className="col-md-4">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Mobile Number 3</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark">{selectedClientForModal.mobile3 || "N/A"}</div>
                  </div>
                  <div className="col-md-6">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Email Address</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark">{selectedClientForModal.email || "N/A"}</div>
                  </div>
                  <div className="col-md-3">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Ratings</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark fw-bold text-warning-600">{selectedClientForModal.ratings || "N/A"} ★</div>
                  </div>
                  <div className="col-md-3">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Designation</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark fw-semibold">{selectedClientForModal.designation || "N/A"}</div>
                  </div>
                  <div className="col-12">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Address</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark">
                      {selectedClientForModal.address || "N/A"}, {selectedClientForModal.city || ""}, {selectedClientForModal.pincode || ""}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Landline</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark">{selectedClientForModal.landline || "N/A"}</div>
                  </div>
                  <div className="col-md-6">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Website Link</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark">
                      {selectedClientForModal.websiteLink ? (
                        <a href={selectedClientForModal.websiteLink.startsWith("http") ? selectedClientForModal.websiteLink : `http://${selectedClientForModal.websiteLink}`} target="_blank" rel="noreferrer">
                          {selectedClientForModal.websiteLink}
                        </a>
                      ) : "N/A"}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Interested Products</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark">
                      {Array.isArray(selectedClientForModal.products)
                        ? selectedClientForModal.products.map((p) => p.name || p.productName || p).join(", ")
                        : "N/A"}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Interested Locations</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark">
                      {Array.isArray(selectedClientForModal.interestedLocations)
                        ? selectedClientForModal.interestedLocations.map((l) => l.name || l.locationName || l).join(", ")
                        : "N/A"}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-top p-16 bg-light radius-bottom-16">
                <button
                  type="button"
                  className="btn btn-secondary radius-8 px-24 font-weight-bold"
                  onClick={() => setShowClientDetailModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Client History Modal */}
      {showClientHistoryModal && selectedClientForModal && (
        <div
          className="modal fade show d-block animate__animated animate__fadeIn"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg animate__animated animate__zoomIn">
            <div className="modal-content radius-16 border-0 shadow-lg">
              <div
                className="modal-header border-bottom py-16 px-24"
                style={{ background: "linear-gradient(135deg, #000000, #bb0007, #ed1c24)" }}
              >
                <h6 className="modal-title text-white mb-0">
                  Followup History: {selectedClientForModal.companyName}
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowClientHistoryModal(false)}
                ></button>
              </div>
              <div className="modal-body p-24">
                <h6 className="text-sm fw-bold mb-12 text-danger-600 uppercase">Followup Log Details</h6>
                <div
                  className="table-responsive shadow-sm radius-8 border scroll-sm"
                  style={{ maxHeight: "350px", overflowY: "auto" }}
                >
                  <table className="table table-sm mb-0">
                    <thead className="bg-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                      <tr>
                        <th className="text-xs fw-bold px-12 py-8">S.No</th>
                        <th className="text-xs fw-bold px-12 py-8">Created At</th>
                        <th className="text-xs fw-bold px-12 py-8">Next Followup Date</th>
                        <th className="text-xs fw-bold px-12 py-8">Status</th>
                        <th className="text-xs fw-bold px-12 py-8">Remark</th>
                        <th className="text-xs fw-bold px-12 py-8">Updated By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientFollowupHistory && clientFollowupHistory.length > 0 ? (
                        clientFollowupHistory.map((h, i) => (
                          <tr key={i}>
                            <td className="text-xs px-12 py-8">{i + 1}</td>
                            <td className="text-xs px-12 py-8">{formatDateTime(h.createdAt || h.date)}</td>
                            <td className="text-xs px-12 py-8">
                              {(h.status === "Todayfolloup" || h.status === "Prospects") && h.date
                                ? <span className="badge bg-info-100 text-info-800 px-8 py-2 radius-4 text-xs">{formatDate(h.date)}</span>
                                : <span className="text-secondary-light">—</span>}
                            </td>
                            <td className="text-xs px-12 py-8">
                              <span className={`badge px-8 py-2 radius-4 text-xs ${h.status === 'Assignes' ? 'bg-primary-100 text-primary-800' :
                                h.status === 'Todayfolloup' ? 'bg-info-100 text-info-800' :
                                  h.status === 'Prospects' ? 'bg-warning-100 text-warning-800' :
                                    h.status === 'Not interested' ? 'bg-danger-100 text-danger-800' :
                                      (h.status === 'Block' || h.status === 'Blocked') ? 'bg-warning-100 text-warning-800' :
                                        h.status === 'Booked' ? 'bg-success-100 text-success-800' :
                                          h.status === 'UnWanted' ? 'bg-danger-100 text-danger-800' : 'bg-secondary-100 text-secondary-800'
                                }`}>
                                {h.status === "Assignes" ? "Assigns" :
                                  h.status === "Todayfolloup" ? "Next Followup" :
                                    h.status === "Prospects" ? "Prospect" :
                                      h.status === "Not interested" ? "Not interested" :
                                        (h.status === "Block" || h.status === "Blocked") ? "Blocked Stall" :
                                          h.status === "Booked" ? "Booked" :
                                            h.status === "UnWanted" ? "Unwanted" : h.status}
                              </span>
                            </td>
                            <td className="text-xs px-12 py-8" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{h.remarks || h.remark || "No comments"}</td>
                            <td className="text-xs px-12 py-8 fw-semibold">{h.createdByName || "Admin"}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center text-xs py-16 text-secondary-light">
                            No previous followup history log details.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer border-top p-16 bg-light radius-bottom-16">
                <button
                  type="button"
                  className="btn btn-secondary radius-8 px-24 font-weight-bold"
                  onClick={() => setShowClientHistoryModal(false)}
                >
                  Close History
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnwantedEnquiriesLayer;
